import { apiPost } from "./api";

const TOKEN_KEY = "clinic_token";
const USER_KEY = "clinic_username";
const ROLE_KEY = "clinic_role";
const REMEMBER_KEY = "clinic_remember";
const HANDOFF_KEY = "clinic_auth_handoff";

type AppContext = "doctor" | "manager" | null;

function getAppContextFromUrl(): AppContext {
  const path = window.location.pathname;
  if (path.startsWith("/doctor")) return "doctor";
  if (path.startsWith("/manager")) return "manager";
  return null;
}

function getNamespacedKey(baseKey: string, context?: AppContext): string {
  // If context is not provided, try to infer from URL.
  // We use undefined to signal "infer", and null to signal "force base/generic".
  const ctx = context === undefined ? getAppContextFromUrl() : context;
  if (ctx) return `${baseKey}_${ctx}`;
  return baseKey;
}

export interface LoginResponse {
  access_token: string;
  role?: string;
  username?: string;
}

function getRememberPreference(): boolean {
  return localStorage.getItem(REMEMBER_KEY) === "1";
}

function setRememberPreference(value: boolean): void {
  localStorage.setItem(REMEMBER_KEY, value ? "1" : "0");
}

function getStorageForRemember(remember: boolean): Storage {
  return remember ? localStorage : sessionStorage;
}

function getTokenFromStorage(storage: Storage, context: AppContext = null): string | null {
  return storage.getItem(getNamespacedKey(TOKEN_KEY, context));
}

function clearStorage(storage: Storage, context: AppContext = null): void {
  const ctx = context || getAppContextFromUrl();
  // Clear specific context if known, otherwise clear base (or maybe clear all? safer to be specific)
  // For logout, we might want to only clear the current app's session
  storage.removeItem(getNamespacedKey(TOKEN_KEY, ctx));
  storage.removeItem(getNamespacedKey(USER_KEY, ctx));
  storage.removeItem(getNamespacedKey(ROLE_KEY, ctx));
}

export function setAuthHandoffToken(token: string): void {
  try {
    localStorage.setItem(
      HANDOFF_KEY,
      JSON.stringify({ token: String(token || ""), at: Date.now() })
    );
  } catch {
    // ignore storage errors
  }
}

function takeAuthHandoffToken(): string | null {
  try {
    const raw = localStorage.getItem(HANDOFF_KEY);
    if (!raw) return null;
    localStorage.removeItem(HANDOFF_KEY);
    const parsed = JSON.parse(raw) as { token?: string; at?: number } | null;
    const token = parsed?.token;
    const at = parsed?.at;
    if (!token || typeof token !== "string") return null;
    if (!at || typeof at !== "number") return null;
    if (Date.now() - at > 60_000) return null; // 60s TTL
    return token;
  } catch {
    return null;
  }
}

function parseJwt(token: string): { exp?: number; role?: string; username?: string } | null {
  if (!token || typeof token !== "string" || token === "null" || token === "undefined") return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (err) {
    console.warn("JWT parse failed:", err);
    return null;
  }
}

function normalizeRoleValue(role?: string | null): string | null {
  if (!role) return null;
  const normalized = role.trim().toLowerCase();
  if (!normalized) return null;

  if (["admin", "administrator", "superadmin", "root"].includes(normalized)) {
    return "admin";
  }
  if (normalized === "front desk" || normalized === "frontdesk") {
    return "reception";
  }
  if (normalized === "clinic" || normalized === "clinic staff") {
    return "clinic";
  }
  return normalized;
}

export async function login(
  username: string,
  password: string,
  remember = true,
  appContext: AppContext = null
): Promise<LoginResponse> {
  const res = (await apiPost("/auth/login", {
    username,
    password,
  })) as LoginResponse;

  setRememberPreference(remember);
  const storage = getStorageForRemember(remember);

  // We explicitly clear/set only the keys for the intended app context
  // This allows multiple sessions to coexist in the same storage if they have different prefixes
  storage.setItem(getNamespacedKey(TOKEN_KEY, appContext), res.access_token);
  storage.setItem(getNamespacedKey(USER_KEY, appContext), res.username ?? username);
  const normalizedRole = normalizeRoleValue(res.role);
  if (normalizedRole) storage.setItem(getNamespacedKey(ROLE_KEY, appContext), normalizedRole);
  return res;
}

export function getToken(): string | null {
  const preferred = getStorageForRemember(getRememberPreference());
  const fallback = preferred === localStorage ? sessionStorage : localStorage;

  // Try to find token in current context
  let token =
    getTokenFromStorage(preferred) ??
    getTokenFromStorage(fallback);

  // If we are in a sub-app (doctor/manager) but didn't find a sub-app token,
  // try to find a generic "clinic" token. This happens when opening modules in new tabs.
  if (!token && getAppContextFromUrl()) {
    token =
      getTokenFromStorage(preferred, null) ??
      getTokenFromStorage(fallback, null);
  }

  if (!token) {
    const handed = takeAuthHandoffToken();
    if (handed) {
      try {
        // If we receive a handoff, we save it to the CURRENT context
        preferred.setItem(getNamespacedKey(TOKEN_KEY), handed);
      } catch {
        // ignore storage errors
      }
      token = handed;
    }
  }

  // Fallback: If we are in a shared path (like /treatments) and didn't find a token in the current context (which is null),
  // try to find a token in 'doctor' or 'manager' namespaces.
  if (!token && !getAppContextFromUrl()) {
    const contexts: AppContext[] = ["doctor", "manager"];
    for (const ctx of contexts) {
      token = getTokenFromStorage(preferred, ctx) ?? getTokenFromStorage(fallback, ctx);
      if (token) break;
    }
  }

  if (!token || token === "null" || token === "undefined") {
    // console.log("getToken: No valid token string");
    return null;
  }

  const payload = parseJwt(token);
  if (!payload) {
    console.log("getToken: Token corrupted");
    return null;
  }

  if (payload.exp && Date.now() > payload.exp * 1000) {
    console.log("getToken: Token expired");
    logout();
    return null;
  }
  // console.log("getToken: Valid token found");
  return token;
}

export function logout(): void {
  // Clear only the current context's session
  clearStorage(localStorage);
  clearStorage(sessionStorage);

  // NOTE: do NOT clear REMEMBER_KEY globally, as it might be shared or valid preference
  // But if we want to be safe:
  // localStorage.removeItem(REMEMBER_KEY); 
}

export function getUsername(): string | null {
  const preferred = getStorageForRemember(getRememberPreference());
  const fallback = preferred === localStorage ? sessionStorage : localStorage;
  const stored = preferred.getItem(USER_KEY) ?? fallback.getItem(USER_KEY);
  if (stored) return stored;
  const token = getToken();
  const payload = token ? parseJwt(token) : null;
  return payload?.username ?? null;
}

export function getRole(): string | null {
  const preferred = getStorageForRemember(getRememberPreference());
  const fallback = preferred === localStorage ? sessionStorage : localStorage;

  // Try current context first, then generic fallback
  const stored =
    preferred.getItem(getNamespacedKey(ROLE_KEY)) ??
    fallback.getItem(getNamespacedKey(ROLE_KEY)) ??
    preferred.getItem(getNamespacedKey(ROLE_KEY, null)) ??
    fallback.getItem(getNamespacedKey(ROLE_KEY, null));

  if (stored) return normalizeRoleValue(stored);

  const token = getToken();
  const payload = token ? parseJwt(token) : null;
  return normalizeRoleValue(payload?.role ?? null);
}
